import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { Sparkles, Download, Heart, TrendingUp, MessageCircleHeart, Flame } from "lucide-react";
import { api, API } from "../lib/api";
import { useLang } from "../lib/i18n";
import { toast } from "sonner";
import { track } from "../lib/ga";

const MOOD_EMOJIS = ["😞", "😐", "🙂", "😊", "🤩"];

// Stable palette mapped to the 6 mascots' emotions
const EMOTION_COLORS = {
  joy:        "#F2CC8F", // sun-gold — Sparky
  calm:       "#81B29A", // sage green
  anger:      "#E07A5F", // Grr terracotta
  anxiety:    "#B08BBB", // Bandit lavender
  confidence: "#F4A261", // Max amber
  fear:       "#8AA1B1", // Hoohoo slate-blue
  sadness:    "#9DB7CF", // Cloudy soft blue
};

function StatCard({ label, value, sub, icon: Icon, tone = "sand", testId }) {
  const tones = {
    sand:  "bg-[#FDFBF7] border-[#E8E6E1]",
    sage:  "bg-[#81B29A]/10 border-[#81B29A]/30",
    peach: "bg-[#E07A5F]/10 border-[#E07A5F]/30",
    gold:  "bg-[#F2CC8F]/15 border-[#F2CC8F]/40",
  };
  return (
    <div className={`rounded-2xl border ${tones[tone]} p-4 md:p-5 flex items-start gap-3`} data-testid={testId}>
      {Icon && (
        <div className="shrink-0 w-10 h-10 rounded-xl bg-white/70 border border-white grid place-items-center text-[#3D405B]">
          <Icon className="w-5 h-5" />
        </div>
      )}
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[#8A8D91] font-semibold">{label}</div>
        <div className="text-2xl md:text-3xl font-heading text-[#3D405B] leading-tight mt-0.5">{value}</div>
        {sub && <div className="text-xs text-[#8A8D91] mt-1">{sub}</div>}
      </div>
    </div>
  );
}

export default function MoodDashboard({ childName = "" }) {
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [insight, setInsight] = useState(null);
  const [loadingInsight, setLoadingInsight] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);

  const load = () => api.get("/stats").then((r) => setStats(r.data)).catch(() => {});

  // Reload on mount and whenever language toggles (labels update)
  useEffect(() => { load(); }, [lang]);

  if (!stats) return null;

  const emotionData = Object.entries(stats.emotion_counts || {}).map(([name, value]) => ({
    name,
    value,
    label: t(`emo.${name}`),
  }));
  const hasEmotions = emotionData.length > 0;

  const generateInsight = async () => {
    setLoadingInsight(true);
    setInsight(null);
    try {
      const { data } = await api.post("/insights/weekly", { lang, child_name: childName });
      track.insightGenerated(lang);
      setInsight(data);
    } catch {
      toast.error(t("dash.insight.error"));
    } finally {
      setLoadingInsight(false);
    }
  };

  const downloadPdf = async () => {
    setLoadingPdf(true);
    try {
      const res = await fetch(`${API}/reports/monthly?lang=${lang}`);
      if (!res.ok) throw new Error("pdf");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = lang === "fr" ? `le-gang-rapport-${new Date().toISOString().slice(0, 7)}.pdf` : `bigfeels-report-${new Date().toISOString().slice(0, 7)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      track.pdfDownloaded(lang);
      toast.success(t("dash.pdf.saved"));
    } catch {
      toast.error(t("dash.pdf.error"));
    } finally {
      setLoadingPdf(false);
    }
  };

  const parentAvgIdx = Math.max(0, Math.min(4, Math.round(stats.parent_avg) - 1));
  const childAvgIdx = Math.max(0, Math.min(4, Math.round(stats.child_avg) - 1));

  return (
    <section className="space-y-6" data-testid="mood-dashboard">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl md:text-3xl text-[#3D405B]">{t("dash.title")}</h2>
          <p className="text-[#8A8D91] text-sm mt-1 max-w-xl">{t("dash.subtitle")}</p>
        </div>
        <button
          type="button"
          onClick={downloadPdf}
          disabled={loadingPdf}
          data-testid="dash-download-pdf"
          className="inline-flex items-center gap-2 rounded-full border border-[#3D405B]/20 bg-white text-[#3D405B] px-4 py-2 text-sm font-medium hover:bg-[#3D405B]/5 transition-colors disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {loadingPdf ? t("dash.pdf.loading") : t("dash.pdf.button")}
        </button>
      </header>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4" data-testid="dash-stats-grid">
        <StatCard
          testId="stat-parent-avg"
          label={t("dash.stat.parent")}
          value={stats.total_moods > 0 ? MOOD_EMOJIS[parentAvgIdx] : "—"}
          sub={stats.total_moods > 0 ? `${stats.parent_avg} / 5` : t("dash.stat.none")}
          icon={Heart}
          tone="peach"
        />
        <StatCard
          testId="stat-child-avg"
          label={t("dash.stat.child")}
          value={stats.total_moods > 0 ? MOOD_EMOJIS[childAvgIdx] : "—"}
          sub={stats.total_moods > 0 ? `${stats.child_avg} / 5` : t("dash.stat.none")}
          icon={TrendingUp}
          tone="sage"
        />
        <StatCard
          testId="stat-streak"
          label={t("dash.stat.streak")}
          value={stats.streak}
          sub={t(stats.streak === 1 ? "dash.stat.streak.one" : "dash.stat.streak.plural")}
          icon={Flame}
          tone="gold"
        />
        <StatCard
          testId="stat-wins"
          label={t("dash.stat.wins")}
          value={stats.wins_count}
          sub={t("dash.stat.wins.sub", { n: stats.worries_count })}
          icon={MessageCircleHeart}
          tone="sand"
        />
      </div>

      {/* Emotion pie + Weekly Bilan */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Pie */}
        <div className="lg:col-span-3 bg-white border border-[#E8E6E1] rounded-3xl p-5 md:p-6" data-testid="dash-pie-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg text-[#3D405B]">{t("dash.pie.title")}</h3>
            <span className="text-[10px] uppercase tracking-widest text-[#8A8D91]">{t("dash.pie.badge")}</span>
          </div>
          {hasEmotions ? (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={emotionData}
                    dataKey="value"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={48}
                    paddingAngle={2}
                    stroke="#FDFBF7"
                    strokeWidth={2}
                  >
                    {emotionData.map((entry) => (
                      <Cell key={entry.name} fill={EMOTION_COLORS[entry.name] || "#8A8D91"} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #E8E6E1", borderRadius: 12, fontSize: 12 }}
                    formatter={(v, n) => [`${v}`, n]}
                  />
                  <Legend
                    iconType="circle"
                    wrapperStyle={{ fontSize: 12, paddingTop: 8, color: "#3D405B" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-[#8A8D91] italic py-8 text-center leading-relaxed" data-testid="dash-pie-empty">
              {t("dash.pie.empty")}
            </div>
          )}
        </div>

        {/* Weekly Bilan */}
        <div className="lg:col-span-2 bg-gradient-to-br from-[#F4F1EA] to-white border border-[#E8E6E1] rounded-3xl p-5 md:p-6 flex flex-col" data-testid="dash-insight-card">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-[#E07A5F]" />
            <span className="text-[10px] uppercase tracking-widest text-[#E07A5F] font-semibold">{t("dash.insight.badge")}</span>
          </div>
          <h3 className="font-heading text-lg text-[#3D405B] mb-2">{t("dash.insight.title")}</h3>
          <p className="text-sm text-[#8A8D91] leading-relaxed mb-4">{t("dash.insight.subtitle")}</p>

          {insight ? (
            <div className="flex-1 text-[#3D405B] text-sm leading-relaxed whitespace-pre-wrap" data-testid="dash-insight-result">
              {insight.summary}
            </div>
          ) : (
            <div className="flex-1" />
          )}

          <button
            type="button"
            onClick={generateInsight}
            disabled={loadingInsight}
            data-testid="dash-generate-insight"
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#3D405B] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#2E3049] transition-colors disabled:opacity-60"
          >
            <Sparkles className="w-4 h-4" />
            {loadingInsight ? t("dash.insight.loading") : insight ? t("dash.insight.regenerate") : t("dash.insight.button")}
          </button>
        </div>
      </div>
    </section>
  );
}
